import { Box, Grid, Stack, Typography, useTheme } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import { useEffect, useMemo, useState } from 'react';
import { selectTranslations } from 'state/reducers/translation.reducers';
import { hasPermission } from 'components/AuthorizationComponent';
import { permissionEnums } from 'constants/permission.constants';
import { getDashboardFees, getPaymentFees } from 'state/reducers/balance.reducer';
import { SmallCard } from 'theme/elements/Cards';
import _ from 'lodash';
import { format } from 'date-fns';
import { IBalanceTypeCategory, IBalanceDashboard, DashboardCategories } from 'constants/balance.constants';
import { useNavigate } from 'react-router-dom';
import { pagesEnum } from 'constants/ui.constants';
import { formatNumber } from 'components/FormCreatorComponent/helpers';

interface TopBoxProps {
	category: IBalanceTypeCategory;
}

export const BottomBox: React.FC<TopBoxProps> = ({ category }) => {
	const theme = useTheme();
	const t = useAppSelector(selectTranslations);
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const chosenSymbol = '₪';

	const path: DashboardCategories = category.name;
	const data = useAppSelector((state) => state.balance[path]);
	const formattedDate = format(new Date(), 'dd.MM.yyyy');

	const { conversionFees, paymentFees, monthlySubscriptions, averageIncomeFromCustomer } = useAppSelector(
		(state: any) => state.balance
	);
	const dashboardFees = { [category.name]: data };
	const totalRevenue = useMemo(() => {
		return conversionFees?.totalFeesNis + paymentFees?.totalFeesNis + monthlySubscriptions?.planRevenues;
	}, [conversionFees, paymentFees, monthlySubscriptions]);

	const getAmount = () => {
		switch (category.name) {
			case DashboardCategories.PAYMENT_FEES:
			case DashboardCategories.CONVERSION_FEES:
				return _.isNil(_.get(data, 'totalFeesNis')) ? 0 : _.get(data, 'totalFeesNis');
			case DashboardCategories.MONTHLY_SUBSCRIPTIONS:
				return _.isNil(_.get(data, 'planRevenues')) ? 0 : _.get(data, 'planRevenues');
		}
		return '';
	};
	const getSymbol = () => {
		switch (category.name) {
			case DashboardCategories.PAYMENT_FEES:
			case DashboardCategories.CONVERSION_FEES:
			case DashboardCategories.MONTHLY_SUBSCRIPTIONS:
				return '₪';
		}
		return '';
	};
	const getAverageSum = () => {
		switch (category.name) {
			case DashboardCategories.PAYMENT_FEES:
				return _.isNil(_.get(data, 'averageDealSumNis')) ? 0 : _.get(data, 'averageDealSumNis');
			case DashboardCategories.CONVERSION_FEES:
				return _.isNil(_.get(data, 'averageDealSum')) ? 0 : _.get(data, 'averageDealSum');
		}
		return '';
	};

	return (
		<Stack sx={{ position: 'relative' }}>
			{dashboardFees && (
				<SmallCard style={{ display: 'flex', flexDirection: 'column' }}>
					<Box
						sx={{ cursor: 'pointer' }}
						onClick={() => {
							navigate('/' + pagesEnum.MAIN_DASHBOARD + '/' + category.name);
						}}
					>
						{[DashboardCategories.MONTHLY_SUBSCRIPTIONS].includes(category.name) ? (
							<Typography variant="captionRegular">
								{t('page.dashboard.dashboardBalances.asOfDate')} {formattedDate}
							</Typography>
						) : (
							<div>&nbsp;</div>
						)}
						{category.name === DashboardCategories.TOTAL_REVENUE ? (
							<Box>
								<Typography variant="h5">
									{`${chosenSymbol}`}
									{formatNumber(Number(`${totalRevenue}`))}
								</Typography>
								<Typography variant="subtitle2">
									{t(`page.dashboard.dashboardBalances.${category.name}`)}
								</Typography>
							</Box>
						) : (
							<Typography variant="h5" sx={{ direction: 'rtl', textAlign: 'left' }}>
								{`${getSymbol()}`}
								{formatNumber(Number(`${getAmount()}`))}
							</Typography>
						)}
					</Box>
					{category.name !== DashboardCategories.TOTAL_REVENUE && (
						<Box display="flex" alignItems="center" gap="10px">
							{category.icon && (
								<img src={`/icons/${category.icon}.svg`} width={16} height={16} alt={category.name} />
							)}
							<Typography variant="subtitle2">
								{t(`page.dashboard.dashboardBalances.${category.name}`)}
							</Typography>
						</Box>
					)}

					{[DashboardCategories.CONVERSION_FEES, DashboardCategories.PAYMENT_FEES].includes(
						category.name
					) && (
						<Box display="flex" flexDirection="column">
							<Box
								sx={{
									display: 'flex',
									justifyContent: 'space-between',
								}}
							>
								<Typography variant="captionRegular">
									{t('page.dashboard.dashboardBalances.amountOfTransactions')}
								</Typography>
								<Typography variant="boldSmall">
									{`${dashboardFees[category.name]?.totalDeals || 0} `}
								</Typography>
							</Box>
							<Box
								sx={{
									display: 'flex',
									justifyContent: 'space-between',
								}}
							>
								<Typography variant="captionRegular">
									{t('page.dashboard.dashboardBalances.transactionAverage')}
								</Typography>
								<Typography variant="boldSmall">
									{`${getSymbol()}`}
									{formatNumber(Number(`${getAverageSum()}`))}
								</Typography>
							</Box>
						</Box>
					)}
					{category.name === DashboardCategories.MONTHLY_SUBSCRIPTIONS && (
						<>
							<Stack>
								<Typography variant="boldSmall">&nbsp;</Typography>
							</Stack>
							<Box
								sx={{
									display: 'flex',
									justifyContent: 'space-between',
								}}
							>
								<Typography variant="captionRegular">
									{t('page.dashboard.dashboardBalances.totalAccounts')}
								</Typography>
								<Typography variant="boldSmall">
									{dashboardFees?.monthlySubscriptions?.numberOfAccount}
								</Typography>
							</Box>
						</>
					)}
					{category.name === DashboardCategories.TOTAL_REVENUE && (
						<>
							<Stack>
								<Typography variant="boldSmall">&nbsp;</Typography>
							</Stack>
							<Box
								display="flex"
								justifyContent="space-between"
								alignItems="center"
								className="asdkjasdlkj"
							>
								<Typography variant="captionRegular">
									{t('page.dashboard.dashboardBalances.averageIncomeFromCustomer')}
								</Typography>
								<Typography variant="boldSmall">
									{`${chosenSymbol}`}
									{formatNumber(Number(`${averageIncomeFromCustomer}`))}
								</Typography>
							</Box>
						</>
					)}
				</SmallCard>
			)}
		</Stack>
	);
};
